import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useCallback } from 'react';
import { supportTokenService } from '@/services/supportTokenService';
import { useCustomAuth } from '@/contexts/CustomAuthContext';

export interface SupportToken {
  id: string;
  token: string;
  expiresAt: Date;
  status: 'active' | 'expired' | 'revoked';
}

interface UseSupportTokenReturn {
  token: SupportToken | null;
  isLoading: boolean;
  error: string | null;
  generateToken: () => Promise<void>;
  revokeToken: () => Promise<void>;
}

/**
 * Hook to manage support token generation and revocation
 * Integrates with real API endpoints
 */
export function useSupportToken(): UseSupportTokenReturn {
  useLanguage();
  const { user } = useCustomAuth();
  const [token, setToken] = useState<SupportToken | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateToken = useCallback(async () => {
    if (!user?.id_user) {
      setError(translateInterfaceText("Usuário não identificado"));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if offline
      if (!navigator.onLine) {
        throw new Error(translateInterfaceText("offline"));
      }

      // Call real API to generate token
      const response = await supportTokenService.generateToken(user.id_user);

      // Token expires in 30 minutes
      const expiresAt = new Date(Date.now() + 30 * 60 * 1000);

      const newToken: SupportToken = {
        id: Math.random().toString(36).substring(7),
        token: response.token,
        expiresAt,
        status: 'active',
      };

      setToken(newToken);
    } catch (err: any) {
      console.error('Erro ao gerar token de suporte:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'unknown';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id_user]);

  const revokeToken = useCallback(async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check if offline
      if (!navigator.onLine) {
        throw new Error(translateInterfaceText("offline"));
      }

      // Revoke token
      setToken({
        ...token,
        status: 'revoked',
      });

      // Clear token after a short delay
      setTimeout(() => {
        setToken(null);
      }, 1000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'unknown';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    token,
    isLoading,
    error,
    generateToken,
    revokeToken,
  };
}
